import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  House, CalendarBlank, ClipboardText, UsersThree, Scissors,
  ChartBar, Sparkle, Gear, SignOut, List, X, Storefront, Users, Megaphone, CreditCard,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LanguageToggle } from "@/i18n/I18nContext";

const NAV = [
  { to: "/dashboard", icon: House, end: true, id: "dashboard" },
  { to: "/dashboard/calendar", icon: CalendarBlank, id: "calendar" },
  { to: "/dashboard/appointments", icon: ClipboardText, id: "appointments" },
  { to: "/dashboard/customers", icon: Users, id: "customers" },
  { to: "/dashboard/staff", icon: UsersThree, id: "staff" },
  { to: "/dashboard/services", icon: Scissors, id: "services" },
  { to: "/dashboard/promotions", icon: Megaphone, id: "promotions" },
  { to: "/dashboard/reports", icon: ChartBar, id: "reports" },
  { to: "/dashboard/assistant", icon: Sparkle, id: "assistant" },
  { to: "/dashboard/billing", icon: CreditCard, id: "billing" },
  { to: "/dashboard/settings", icon: Gear, id: "settings" },
];

export default function DashboardLayout() {
  const { user, organization, logout } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const plan = organization?.plan;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-7">
        <div className="flex items-center gap-2.5">
          <img src="/bellaos-logo.png" alt="BellaOS" className="h-10 w-10 object-contain" />
          <div>
            <div className="font-heading font-extrabold text-[#1A1A1A] text-lg leading-none tracking-tight">BellaOS</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#6B6A67] mt-1">{organization?.type || "salon"} suite</div>
          </div>
        </div>
        {plan && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: plan === "elite" ? "#D4AF3722" : "#2C3E3510", color: plan === "elite" ? "#9a7d1e" : "#2C3E35" }}>
            {plan === "elite" && <Sparkle size={11} weight="fill" />} {lang === "es" ? `Plan ${plan === "trial" ? "prueba" : plan}` : `${plan} plan`}
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.id}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.id}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-[#2C3E35] text-[#FDFBF9]"
                    : "text-[#3a3a38] hover:bg-[#E8E4DF]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={19} weight={isActive ? "fill" : "regular"} />
                  {t.dnav[item.id]}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-[#E5E2DC] space-y-2">
        <div className="px-2"><LanguageToggle /></div>
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-[#B86B5A] text-white flex items-center justify-center text-xs font-bold">{initials}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A1A1A] truncate">{user?.name}</div>
            <div className="text-xs text-[#6B6A67] truncate">{organization?.name}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="p-2 rounded-lg text-[#6B6A67] hover:bg-[#E8E4DF] hover:text-[#B86B5A] transition-colors"
            title={t.dnav.signout}
          >
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF9] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-[#E5E2DC] fixed inset-y-0 left-0">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-white border-r border-[#E5E2DC]">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-[#E5E2DC] px-4 py-3 flex items-center justify-between">
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-[#E8E4DF]">
            {open ? <X size={20} /> : <List size={20} />}
          </button>
          <div className="font-heading font-extrabold tracking-tight">BellaOS</div>
          <div className="h-8 w-8 rounded-full bg-[#B86B5A] text-white flex items-center justify-center text-xs font-bold">{initials}</div>
        </header>
        <main className="p-5 sm:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
